import {
  Autocomplete,
  Box,
  createFilterOptions,
  TextField,
} from "@mui/material";
import { Create } from "@refinedev/mui";
import { useForm } from "@refinedev/react-hook-form";
import { useEffect, useState } from "react";
import {
  handleCommonCreateRequest,
  handleCommonFindRequest,
  handleCommonGetRequest,
} from "rest-data-provider/utils/tools";
import { useAutocomplete } from "@refinedev/mui";
import { config } from "config";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { format, parse } from "date-fns";

interface ICategory {
  id: number;
  name: string;
}

export const UserBadgesCreate = () => {
  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    formState: { errors },
    setValue,
    handleSubmit,
    setError,
    clearErrors,
  } = useForm();

  const defaultDate = parse("2099-01-01", "yyyy-MM-dd", new Date());

  const [userId, setUserId] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [badgeId, setBadgeId] = useState<string>("");
  const [issueDate, setIssueDate] = useState(new Date());
  const [expirationDate, setExpirationDate] = useState(defaultDate);

  useEffect(() => {
    const formatIssueDate = format(issueDate, "yyyy-MM-dd");
    const formatExpirationDate = format(expirationDate, "yyyy-MM-dd");

    setValue("issueDate", formatIssueDate);
    setValue("expirationDate", formatExpirationDate);
    setValue("badgeId", badgeId);
  }, [badgeId, expirationDate, issueDate, setValue]);

  const { autocompleteProps } = useAutocomplete<ICategory>({
    resource: "badge",
    pagination: {
      pageSize: config().relationPageSize,
    },
    filters: [
      {
        field: "status",
        operator: "eq",
        value: 0,
      },
    ],
    sorters: [
      {
        field: "name",
        order: "asc",
      },
    ],
  });

  const clearUserIdEmail = () => {
    setUserId("");
    setEmail("");
  };

  const getEmail = async () => {
    if (!userId) {
      clearUserIdEmail();
      return;
    }

    const data: any = await handleCommonGetRequest("users", userId);
    if (data && data.email) {
      setEmail(data.email);
      setValue("email", data.email);
      return true;
    } else {
      clearUserIdEmail();
      return true;
    }
  };

  const getUserId = async () => {
    if (!email) {
      clearUserIdEmail();
      return;
    }

    const data: any = await handleCommonFindRequest("users", [
      {
        field: "email",
        value: `${email}`,
        operator: "eq",
      },
    ]);
    if (data.data.length) {
      setUserId(data.data[0].id);
      setValue("userId", data.data[0].id);
      return true;
    } else {
      window.alert("No current user");
      clearUserIdEmail();
      return false;
    }
  };

  const renderUserId = () => {
    return (
      <TextField
        {...register("userId", {
          required: "This field is required",
          validate: () => getEmail(),
          onBlur: () => {
            getEmail();
          },
        })}
        error={!!(errors as any)?.userId}
        helperText={(errors as any)?.userId?.message}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        type="text"
        label="UserId"
        name="userId"
        value={userId}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setUserId(event.target.value);
        }}
      />
    );
  };

  const renderEmail = () => {
    return (
      <TextField
        {...register("email", {
          required: "This field is required",
          validate: () => getUserId(),
          onBlur: () => {
            getUserId();
          },
        })}
        error={!!(errors as any)?.email}
        helperText={(errors as any)?.email?.message}
        margin="normal"
        fullWidth
        InputLabelProps={{ shrink: true }}
        type="text"
        label="Email"
        name="email"
        value={email}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setEmail(event.target.value);
        }}
      />
    );
  };

  const filterOptions = createFilterOptions({
    stringify: (option: any) => option.name,
  });

  const renderBadge = () => {
    return (
      <Autocomplete
        {...autocompleteProps}
        onInputChange={(event, value) => {}}
        getOptionLabel={(item) => item.name}
        onChange={(_, value: any) => {
          const selectedId = value?.id || "";
          setBadgeId(selectedId);
          if (!selectedId) {
            setError("badgeId", {
              type: "manual",
              message: "Badge is required",
            });
          } else {
            clearErrors("badgeId");
          }
        }}
        placeholder="Select a badge"
        filterOptions={filterOptions}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              label="Badge Name"
              margin="normal"
              variant="outlined"
              required
              error={!!errors.badgeId}
              helperText={errors.badgeId ? "This field is required" : ""}
            />
          );
        }}
      />
    );
  };

  const issueDateChangeHandler = (e: any) => {
    setIssueDate(e);
  };

  const expirationDateChangeHandler = (e: any) => {
    setExpirationDate(e);
  };

  const renderIssue = () => {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          sx={{ marginTop: "15px" }}
          label="Issue Date"
          value={issueDate}
          onChange={issueDateChangeHandler}
        />
      </LocalizationProvider>
    );
  };

  const renderExpiration = () => {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          sx={{ marginTop: "25px" }}
          label="Expiration Date"
          value={expirationDate}
          onChange={expirationDateChangeHandler}
        />
      </LocalizationProvider>
    );
  };

  const onSubmit = handleSubmit(async (data) => {
    if (!data.badgeId) {
      setError("badgeId", {
        type: "manual",
        message: "Badge is required",
      });
      return;
    }

    delete data.email;
    await handleCommonCreateRequest("user-badge-relations", data);

    window.history.back();
  });

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        ...saveButtonProps,
        onClick: onSubmit,
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        {renderUserId()}
        {renderEmail()}
        {renderBadge()}
        {renderIssue()}
        {renderExpiration()}
      </Box>
    </Create>
  );
};
